import { useEffect, useReducer } from "react";
import { useTheme } from "styled-components";
import { BreakpointOptions } from "../../types/theming";
import { ViewportResult } from "./useViewport.types";
import { ViewportOperator } from "./ViewportOperator";

export default function useViewport(): ViewportResult {
	const [viewportResult, dispatch] = useReducer(
		updateState,
		new ViewportOperator("xs")
	);

	const theme = useTheme();
	const breakpoints = theme?.breakpoints;

	let sm = 576;
	let md = 768;
	let lg = 992;
	let xl = 1200;
	let xxl = 1400;

	if (breakpoints) {
		sm = breakpoints.sm;
		md = breakpoints.md;
		lg = breakpoints.lg;
		xl = breakpoints.xl;
		xxl = breakpoints.xxl;
	}

	function updateState(
		state: ViewportResult,
		newState: ViewportResult
	): ViewportResult {
		// New state is always a true reflection, so no need to blend with existing state obj.
		return newState;
	}

	const getMediaQuery = (min: number, max?: number) => {
		return max !== undefined
			? `(min-width: ${min}px) and (max-width: ${max - 1}px)`
			: `(min-width: ${min}px)`;
	};

	useEffect(() => {
		const handleChange = () => {
			const mediaToBreakpoint = (): BreakpointOptions => {
				if (xxLargeMedia.matches) return "xxl";
				if (xLargeMedia.matches) return "xl";
				if (largeMedia.matches) return "lg";
				if (mediumMedia.matches) return "md";
				if (smallMedia.matches) return "sm";
				return "xs";
			};
			dispatch(new ViewportOperator(mediaToBreakpoint()));
		};

		const smallMedia = window.matchMedia(getMediaQuery(sm, md));
		const mediumMedia = window.matchMedia(getMediaQuery(md, lg));
		const largeMedia = window.matchMedia(getMediaQuery(lg, xl));
		const xLargeMedia = window.matchMedia(getMediaQuery(xl, xxl));
		const xxLargeMedia = window.matchMedia(getMediaQuery(xxl));

		/*
    We only need to add listeners for every 'other' breakpoint, because an active
    breakpoint also fires when it becomes inactive. For example, a 'large' breakpoint
    will fire the change handler when moving over to medium or xLarge, so a single handler
    attached to largeMedia will cover us on both sides.
    */
		smallMedia.addEventListener("change", handleChange);
		largeMedia.addEventListener("change", handleChange);
		xxLargeMedia.addEventListener("change", handleChange);

		// Set initial state.
		handleChange();

		return () => {
			smallMedia.removeEventListener("change", handleChange);
			largeMedia.removeEventListener("change", handleChange);
			xxLargeMedia.removeEventListener("change", handleChange);
		};
	}, [sm, md, lg, xl, xxl]);

	return viewportResult;
}

export { useViewport };
